import React, { useMemo, useState, useContext } from 'react';
import PropTypes from 'prop-types';
import { isDirty as lensIsDirty } from '@totalsoft/change-tracking';
import { get, set, useChangeTrackingLens } from '@totalsoft/change-tracking-react';
import { greaterThan, required, stopOnFirstFailure } from '@totalsoft/pure-validations';
import { isValid, useDirtyFieldValidation } from '@totalsoft/pure-validations-react';
import { useMutation } from 'features/common/security/hooks';
import { useSimpleError, useAuthorization } from 'hooks';
import { IconButton, TextField, Typography } from '@totalsoft/rocket-ui';
import { useTranslation } from 'react-i18next';
import { UPDATE_RENEWAL_INSURANCE_QUOTATIONS } from '../../../../mutations';
import { Grid, Tooltip } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SaveIcon from '@mui/icons-material/Save';
import { emptyString } from 'constants/defaultValues';
import { useCallback } from 'react';
import { useEffect } from 'react';
import { valueOrDefault } from 'utils/functions';
import InsuranceDataContext from 'features/insuranceDocument/stepper/insuranceDataContext';
import AuthorizationCodes from 'features/common/constants/authorizationCodes';
import LoadingWrapper from 'components/common/LoadingWrapper';
import {
  RenewalQuotationAmount,
  RenewalQuotationAmountContainer,
  RenewalQuotationCard,
  RenewalQuotationEditIcon,
  RenewalQuotationErrorIcon,
  RenewalQuotationInfoIcon,
  RenewalQuotationInsurerName
} from 'features/policyRenewal/components/PolicyRenewalStyles';

const validator = [required, greaterThan(0)] |> stopOnFirstFailure;

const PolicyRenewalItemQuotationComponent = ({ quotation, index, fetching, running }) => {
  const { documentTypeId, documentStateId } = useContext(InsuranceDataContext);
  const hasRightToEditRenewal = useAuthorization(AuthorizationCodes.hasRightToEditRenewal, documentTypeId, documentStateId);
  const { t } = useTranslation();
  const showError = useSimpleError();

  const [inEdit, setInEdit] = useState(false);
  const [saving, setSaving] = useState(false);

  const [lens, dirtyInfo, resetLens] = useChangeTrackingLens();
  const [validation, validate] = useDirtyFieldValidation(validator);

  useEffect(() => {
    resetLens(quotation.insuranceAmount);
    setInEdit(false);
    setSaving(false);
  }, [quotation.insuranceAmount, resetLens]);

  // #region Memos

  const documentId = useMemo(() => quotation.documentId, [quotation.documentId]);

  const initialAmountInfo = useMemo(
    () =>
      quotation.initialAmount || quotation.sourceOfferId
        ? `${t('Offer.PolicyRenewalList.Field.InitialValue')}: ${
            quotation.initialAmount ? `${quotation.initialAmount} ${quotation.currencyCode}` : 'N/A'
          }${quotation.additionalInfo ? ` (${quotation.additionalInfo})` : emptyString}`
        : emptyString,
    [quotation.initialAmount, quotation.sourceOfferId, quotation.currencyCode, quotation.additionalInfo, t]
  );

  const saveDisabled = useMemo(() => saving || !lensIsDirty(dirtyInfo), [saving, dirtyInfo]);

  const loading = useMemo(() => quotation.runningYN, [quotation.runningYN]);

  // #endregion

  // #region Update quotation

  const [updateRenewalInsuranceQuotations] = useMutation(UPDATE_RENEWAL_INSURANCE_QUOTATIONS, {
    onError: error => {
      showError(error);
      setSaving(false);
    }
  });

  const handleUpdateRenewalQuotations = useCallback(() => {
    if (saveDisabled) return;

    const insuranceAmount = lens |> get;
    if (!validate(insuranceAmount)) {
      return;
    }

    const quotationsToUpdate = [
      {
        offerId: quotation.offerId,
        insuranceAmount
      }
    ];

    updateRenewalInsuranceQuotations({
      variables: {
        input: {
          documentId,
          offers: quotationsToUpdate
        }
      }
    });
    setSaving(true);
  }, [quotation, lens, documentId, validate, updateRenewalInsuranceQuotations, saveDisabled]);

  // #endregion

  // #region Handlers

  const handleEdit = useCallback(() => setInEdit(true), []);

  const handleCancel = useCallback(() => {
    if (saving) return;
    resetLens(quotation.insuranceAmount);
    setInEdit(false);
  }, [quotation.insuranceAmount, resetLens, saving]);

  // #endregion

  return (
    <Grid item container xs={12} md={6} lg={4} xl={3} key={`${documentId}_${index}`}>
      <RenewalQuotationCard disablePadding>
        <Grid item container xs={12} justifyContent={'space-between'} alignItems={'center'} wrap={'nowrap'}>
          <RenewalQuotationInsurerName>{quotation.insurerName}</RenewalQuotationInsurerName>
          {initialAmountInfo && (
            <Tooltip title={initialAmountInfo}>
              <RenewalQuotationInfoIcon fontSize='small' />
            </Tooltip>
          )}
          {!initialAmountInfo && quotation.additionalInfo && (
            <Tooltip title={quotation.additionalInfo || emptyString}>
              <RenewalQuotationErrorIcon fontSize='small' />
            </Tooltip>
          )}
        </Grid>
        <Grid item container xs={12} alignItems={'flex-end'} sx={{ lineHeight: 1 }}>
          <Grid container item xs={12} sm={5} pt={1}>
            <strong>{t('Offer.PolicyRenewalList.Field.BonusMalus')}:</strong>
            <span>{quotation.bonusMalus || 'N/A'}</span>
          </Grid>
          <RenewalQuotationAmountContainer
            item
            container
            xs={12}
            sm={7}
            justifyContent={'space-between'}
            alignItems={'flex-end'}
            wrap={'nowrap'}
          >
            <LoadingWrapper loading={loading}>
              {!inEdit && (
                <>
                  <Grid item>
                    <strong>{t('Offer.PolicyRenewalList.Field.InsuranceAmount')}: </strong>
                    <RenewalQuotationAmount>{`${quotation.insuranceAmount || '-'} ${quotation.currencyCode}`}</RenewalQuotationAmount>
                  </Grid>
                  <Grid item>
                    {hasRightToEditRenewal && (
                      <Tooltip title={t('Tooltips.Edit')}>
                        <RenewalQuotationEditIcon color={'primary'} onClick={handleEdit} />
                      </Tooltip>
                    )}
                  </Grid>
                </>
              )}
              {inEdit && (
                <Grid item container alignItems={'center'}>
                  <TextField
                    id={`insuranceAmount_${quotation.offerId}`}
                    isNumeric
                    fullWidth
                    onChange={lens |> set}
                    disabled={saving || fetching || running}
                    value={lens |> get |> valueOrDefault(emptyString)}
                    error={!isValid(validation)}
                    endAdornment={
                      <>
                        <Typography fontSize='small'>{quotation.currencyCode}</Typography>
                        <IconButton
                          size='tiny'
                          variant='text'
                          onClick={handleUpdateRenewalQuotations}
                          tooltip={t('Tooltips.Save')}
                          disabled={saveDisabled}
                        >
                          <SaveIcon fontSize='small' />
                        </IconButton>
                        <IconButton size='tiny' variant='text' color='error' onClick={handleCancel} tooltip={t('Tooltips.Cancel')}>
                          <CloseIcon fontSize='small' />
                        </IconButton>
                      </>
                    }
                  />
                </Grid>
              )}
            </LoadingWrapper>
          </RenewalQuotationAmountContainer>
        </Grid>
      </RenewalQuotationCard>
    </Grid>
  );
};

PolicyRenewalItemQuotationComponent.propTypes = {
  quotation: PropTypes.object.isRequired,
  index: PropTypes.number.isRequired,
  fetching: PropTypes.bool,
  running: PropTypes.bool
};
export default PolicyRenewalItemQuotationComponent;
